import * as Sentry from "@sentry/nextjs"; //  Sentry
import { safeJsonStringify } from "@/utils/json";

/**
 * Call this after user logs in (client-side). Sets the active user in Sentry.
 *
 * @param user - The user object with `id`, `email`, and `username` properties.
 */
export function setSentryUser(user: {
  id: string;
  email?: string;
  username?: string;
}) {
  // top-level setUser writes to the active isolation scope
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
}

/**
 * Call on logout. Clears the active user in Sentry.
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Capture an exception with optional tags & context.
 *
 * @param error - The error to capture.
 * @param options - Optional configuration.
 * @param options.level - The severity level of the error (default: "error").
 * @param options.tags - Additional key-value pairs to attach to the event.
 * @param options.extras - Additional key-value pairs to attach to the event as context.
 */
export function captureError(
  error: unknown,
  options?: {
    level?: Sentry.SeverityLevel;
    tags?: Record<string, string>;
    context?: Record<string, Record<string, unknown>>;
    extra?: Record<string, unknown>;
  }
) {
  function isIgnorableError(error: unknown): boolean {
    const message =
      error instanceof Error
        ? error.message
        : typeof error === "object" && error !== null
          ? safeJsonStringify(error)
          : String(error);

    return (
      message.includes("tx_attempts_exceeded") ||
      message.includes("tx_ack_timeout")
    );
  }

  // 🚫 Don't report ignorable errors to Sentry
  if (isIgnorableError(error)) {
    console.warn("Ignored error:", safeJsonStringify(error));
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel(options?.level ?? "error");

    if (options?.tags) {
      for (const [k, v] of Object.entries(options.tags)) {
        scope.setTag(k, v);
      }
    }

    if (options?.context) {
      for (const [k, v] of Object.entries(options.context)) {
        scope.setContext(k, v);
      }
    }

    if (options?.extra) {
      scope.setExtras({ ...options.extra });
    }

    let normalizedError: Error;

    // 🔍 Check if the captured error is a real Error instance
    if (error instanceof Error) {
      normalizedError = error; // Already an Error, use as-is
    } else {
      // 🛠 Convert non-Error types (like plain objects) to Error instance
      const stringified =
        typeof error === "object"
          ? safeJsonStringify(error, 2)
          : String(error);

      normalizedError = new Error(`Non-Error captured: ${stringified}`);

      // 🗂 Attach the original non-Error object to Sentry for inspection
      scope.setExtra("originalError", error as Record<string, unknown>);
    }

    // 🚨 Finally, capture the normalized error with full scope context
    Sentry.captureException(normalizedError);
  });
}

/**
 * Add a breadcrumb for contextual info.
 *
 * @param options - The breadcrumb options.
 */
export const addBreadcrumb = Sentry.addBreadcrumb;

/**
 * Wrap async code in a performance transaction.
 *
 * @param name - The name of the transaction.
 * @param fn - The async function to wrap.
 */
export async function withTransaction<T>(name: string, fn: () => Promise<T>) {
  await Sentry.startSpan({ name }, fn);
}
